// Maps the holistic Intelligence Report endpoint's rows onto the list's UI shape. This is the only
// place that knows the endpoint's field names, so a contract change stays a one-file fix.
//
// API row: { id, productName, productURL|null,
//            audienceReport|culturalReport|competitiveReport: { requested?, id?, status,
//              progress?, message?, <count field>?, error? },
//            createdBy: { accountId, name, avatarUrl? }, createdAt: 'YYYY-MM-DD HH:mm:ss' }
//   `progress` (0–100), `message` (current phase), the module's count (INTEL_MODULES
//   `countField`: audienceReport.segmentCount / competitiveReport.competitorCount; Cultural has
//   none) and `error` (failure reason) are optional and may be missing.
//   `id` is the module's own record — for the Audience layer an EXISTING Audience (followed with
//   the Audiences endpoints, never re-created). `requested: false` = the report didn't ask for
//   that layer. The Audience layer's `status` is the Audience's own (Initiated / Processing /
//   SegmentsReady / Completed / Failed). The Competitive layer's `id` / `status` are the
//   Competitive Intel request's own (`success` = done, `failed`, `processing`, …).
// UI row:  { id, name, url, urlLabel, layers: { audience, competitive, cultural: { id,
//            requested, status, progress, message, count, error } },
//            createdBy: { name, initials, tone, avatarUrl },
//            createdAt: ISO string }

import dayjs from '../../../utils/dayjs';
import { INTEL_MODULES, REPORT_STATUS } from '../static/constants';

const STATUS_FROM_API = {
  succeeded: REPORT_STATUS.READY,
  ready: REPORT_STATUS.READY,
  running: REPORT_STATUS.GENERATING,
  generating: REPORT_STATUS.GENERATING,
  pending: REPORT_STATUS.GENERATING,
  queued: REPORT_STATUS.GENERATING,
  failed: REPORT_STATUS.FAILED,
  // Audience statuses (Audiences/utils/audienceUtils AUDIENCE_STATUS), lower-cased.
  initiated: REPORT_STATUS.GENERATING,
  processing: REPORT_STATUS.GENERATING,
  segmentsready: REPORT_STATUS.GENERATING,
  completed: REPORT_STATUS.READY,
  // Competitive Intel request statuses (legacy CompetitiveIntel list / useTaskPolling); `success`
  // is also what legacy audiences send for Completed.
  success: REPORT_STATUS.READY,
  validating: REPORT_STATUS.GENERATING,
  validated: REPORT_STATUS.GENERATING,
  discovering: REPORT_STATUS.GENERATING,
  discovered: REPORT_STATUS.GENERATING,
  cancelling: REPORT_STATUS.GENERATING,
  cancelled: REPORT_STATUS.FAILED,
  // An archived Competitive Intel request is finished and no longer usable in the report.
  archived: REPORT_STATUS.FAILED,
};

// Avatar colour pairs in intelligenceReport.scss (.ir-avatar--<tone>).
const AVATAR_TONES = ['neutral', 'pink', 'green', 'warning', 'info', 'blue'];

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/** Unknown or missing statuses read as "still generating" rather than breaking the row. */
export const normalizeReportStatus = (status) =>
  STATUS_FROM_API[String(status || '').toLowerCase()] || REPORT_STATUS.GENERATING;

export const toProgress = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(100, Math.max(0, Math.round(number))) : null;
};

export const toCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

/**
 * One layer's API object → { id, requested, status, progress, message, count, error }
 * (missing → null; `requested` defaults to true so rows without it behave as before). `count`
 * is read from the module's `countField` (a generic `count` still works).
 */
export const normalizeLayer = (layer = {}, countField = 'count') => {
  const requested = layer?.requested !== false;
  return {
    id: layer?.id ?? null,
    requested,
    status: requested ? normalizeReportStatus(layer?.status) : REPORT_STATUS.NOT_REQUESTED,
    progress: toProgress(layer?.progress),
    message: layer?.message || null,
    count: toCount(layer?.[countField] ?? layer?.count),
    error: layer?.error || null,
  };
};

export const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

const toneFor = (accountId, name) => {
  const seed = Number.isFinite(Number(accountId))
    ? Number(accountId)
    : [...String(name)].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_TONES[Math.abs(seed) % AVATAR_TONES.length];
};

const toIsoDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, API_DATE_FORMAT, true);
  const date = parsed.isValid() ? parsed : dayjs(value);
  return date.isValid() ? date.toISOString() : null;
};

/** "https://www.sony.com/wh-1000xm6/" → "sony.com/wh-1000xm6" (the list's muted URL line). */
export const urlLabelOf = (url) => {
  if (!url) return null;
  return (
    String(url)
      .trim()
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/[?#].*$/, '')
      .replace(/\/+$/, '') || null
  );
};

/**
 * @param {object} raw - One row from the holistic endpoint.
 * @returns {object} The list's UI row.
 */
export function normalizeReport(raw = {}) {
  const creatorName = raw.createdBy?.name || '';
  const url = raw.productURL || null;
  return {
    id: raw.id,
    name: raw.productName || 'Untitled report',
    url,
    urlLabel: urlLabelOf(url),
    // The report's campaign objective (awareness / engagement / conversion). The list endpoint
    // doesn't send it yet (AAPU-7257 asks for it); the strategy page titles itself from it.
    objective: raw.objective ?? null,
    layers: INTEL_MODULES.reduce(
      (acc, { key, reportField, countField }) => ({
        ...acc,
        [key]: normalizeLayer(raw[reportField], countField),
      }),
      {}
    ),
    createdBy: {
      name: creatorName,
      initials: initialsOf(creatorName),
      tone: toneFor(raw.createdBy?.accountId, creatorName),
      avatarUrl: raw.createdBy?.avatarUrl || null,
    },
    createdAt: toIsoDate(raw.createdAt),
  };
}
